import { Workbooks } from '../../content/resources/Workbooks';
import { Flows } from '../../flow/resources/Flows';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { FlowRevision } from '../../revisionHistoryFlow/serverApi/types/FlowRevision';
import { IWorkbookRevision } from '../../revisionHistoryWorkbook/serverApi/types/IWorkbookRevision';
import { L10n } from '../../ts/L10n/L10n';
import { DatasourceContentType, FlowContentType, IContentType, WorkbookContentType } from '../../ts/models/ContentTypes';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { RevertFlowResult } from '../../ts/ServerApi/requests/RevertFlowRequest';
import { IRevertWorkbookResult } from '../../ts/ServerApi/requests/RevertWorkbookRequest';
import { DownloadActions } from '../../ts/services/download/DownloadActions';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import {
  openReactActionDialog,
  ReactActionDialogOptions
} from '~commonInfra/dialogs/ReactActionDialog';
import { Messages } from '~src/codegen/Localize';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { ServiceLocator } from '~src/app/ServiceLocator';
import {
  RevisionHistoryDialogBody,
  RevisionHistoryDialogBodyProps,
  RevisionHistoryDialogBodyState,
} from '~src/revisionHistory/components/RevisionHistoryDialogBody';
import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import {RevisionHistoryActionAngular} from '~src/revisionHistory/services/RevisionHistoryActionAngular';
import {FeatureFlags} from '~src/ts/FeatureFlags';
import { CheckConnectionCredentialsAction } from '~src/connectivity/services/CheckConnectionCredentialsAction';

class RevisionHistoryAction {

  private showContentRevisionHistory(content: INamedItemInfo, contentType: IContentType): Promise<IResult> {

    let dialogDescription = '';
    let action; // 10.0 dialog action for workbooks should be one-click revert. Otherwise it's download revision.
    let actionName: string;
    const elidedContentName = {NAME: L10n.elideIfLong(content.name)};
    switch (contentType.name) {
      case WorkbookContentType.name:
        action = this.getRevertWorkbookAction;
        actionName = Messages.revisionHistoryDialogSubmitRestore();
        dialogDescription = Messages.revisionHistoryDialogWorkbookDescription(elidedContentName);
        break;
      case DatasourceContentType.name:
        action = this.getDownloadRevisionAction;
        actionName = Messages.revisionHistoryDialogSubmitDownload();
        dialogDescription = Messages.revisionHistoryDialogDatasourceDescription(elidedContentName);
        break;
      case FlowContentType.name:
        action = this.getRevertFlowAction;
        actionName = Messages.revisionHistoryDialogSubmitRestore();
        dialogDescription = Messages.revisionHistoryDialogFlowDescription(elidedContentName);
        break;
      default:
        throw new Error(Messages.revisionHistoryUnsupportedContentType());
    }
    const revisionHistoryDialogOptions: ReactActionDialogOptions<RevisionHistoryDialogBodyProps, RevisionHistoryDialogBodyState, IResult> = {
        title: Messages.revisionHistoryDialogTitle(),
        initialBodyProps: {contentType, dialogDescription, contentItemId: content.id},
        initialDialogState: {selectedItem: null, deletedItem: null},
        action: action.bind(this),
        bodyComponent: RevisionHistoryDialogBody,
        dismissDialogOnActionFailed: false,
        actionName: actionName,
        actionDisabled: this.isRestoreButtonDisabled,
        dialogWidth: TabDialogWidth.width600px,
        isActionDelete: false
    };
    const instance = openReactActionDialog(revisionHistoryDialogOptions);
    return Promise.resolve(instance.result);
  }

  private getRevertWorkbookAction(props: RevisionHistoryDialogBodyProps,
                                  state: RevisionHistoryDialogBodyState,
                                  updateState: React.Dispatch<MergeOrUpdateStateAction<RevisionHistoryDialogBodyState>>): PromiseLike<IResult> {
      const revision: IWorkbookRevision = <IWorkbookRevision>state.selectedItem;
      return Workbooks.revertWorkbook(props.contentItemId, revision.versionNumber)
        .then((result: IRevertWorkbookResult): Promise<IRevertWorkbookResult> => {
            if (result.failedConnection) {
              const checkCredentialsDialogs = CheckConnectionCredentialsAction.showCheckCredentialsDialogs(result.failedConnection, revision);
              return Promise.resolve(checkCredentialsDialogs);
            } else {
              return Promise.resolve(result);
            }
          })
        .then((result: IRevertWorkbookResult) => {
            if (ResultHelper.hasErrors(result)) {
              updateState((prev) => ({...prev, errorMessage: Messages.revisionHistoryActionErrorForWorkbook()}));
              return Promise.reject(result);
            } else {
              ToasterService.instance.pop({ type: ToastType.Info, text: Messages.revisionHistoryActionSuccessForWorkbook() });
              if (result.scheduledExtractCredentialsMayNotBeRestored) {
                ToasterService.instance.pop({ type: ToastType.Info, text: Messages.revisionHistoryActionSuccessGoToConnections() });
              }
              return Promise.resolve(result);
            }
          });
  }

  private getDownloadRevisionAction(props: RevisionHistoryDialogBodyProps,
                                    state: RevisionHistoryDialogBodyState,
                                    updateState: React.Dispatch<MergeOrUpdateStateAction<RevisionHistoryDialogBodyState>>): PromiseLike<IResult> {
      DownloadActions.downloadContentRevision(state.selectedItem);
      return Promise.resolve({} as any);
  }

  private getRevertFlowAction(props: RevisionHistoryDialogBodyProps,
                              state: RevisionHistoryDialogBodyState,
                              updateState: React.Dispatch<MergeOrUpdateStateAction<RevisionHistoryDialogBodyState>>): PromiseLike<IResult> {
      const revision: FlowRevision = <FlowRevision>state.selectedItem;
      return Flows.revertFlow(props.contentItemId, revision.versionNumber)
        .then((result: RevertFlowResult): Promise<RevertFlowResult> => {
          if (result.failedConnection) {
            return Promise.resolve(CheckConnectionCredentialsAction.showCheckCredentialsDialogs(result.failedConnection, revision));
          } else {
            return Promise.resolve(result);
          }
        })
        .then((result: RevertFlowResult) => {
          if (ResultHelper.hasErrors(result)) {
            updateState((prev) => ({...prev, errorMessage: Messages.revisionHistoryActionErrorForFlow()}));
            return Promise.reject(result);
          } else {
            ToasterService.instance.pop({ type: ToastType.Info, text: Messages.revisionHistoryActionSuccessForFlow() });
            return Promise.resolve(result);
          }
        });
  }

  public showDataSourceRevisionHistory(datasource: INamedItemInfo): Promise<IResult> {
    return FeatureFlags.revisionHistoryDialogMigrationFF ?
      this.showContentRevisionHistory(datasource, DatasourceContentType) :
      Promise.resolve(this.getRevisionHistoryActionAngular().showContentRevisionHistory(datasource, DatasourceContentType));
  }

  public showWorkbookRevisionHistory(workbook: INamedItemInfo): Promise<IResult> {
    return FeatureFlags.revisionHistoryDialogMigrationFF ?
      this.showContentRevisionHistory(workbook, WorkbookContentType) :
      Promise.resolve(this.getRevisionHistoryActionAngular().showContentRevisionHistory(workbook, WorkbookContentType));
  }

  public showFlowRevisionHistory(flow: INamedItemInfo): Promise<IResult> {
    return FeatureFlags.revisionHistoryDialogMigrationFF ?
      this.showContentRevisionHistory(flow, FlowContentType) :
      Promise.resolve(this.getRevisionHistoryActionAngular().showContentRevisionHistory(flow, FlowContentType));
  }

  private getRevisionHistoryActionAngular(): RevisionHistoryActionAngular{
    return ServiceLocator.get<RevisionHistoryActionAngular>('RevisionHistoryActionAngular');
  }

  private isRestoreButtonDisabled(props: RevisionHistoryDialogBodyProps, state: RevisionHistoryDialogBodyState) {
    return state.selectedItem === null;
  }
}

export const RevisionHistoryActionInstance = new RevisionHistoryAction();
export type {RevisionHistoryAction};
